<script>
    import { onDestroy, onMount } from "svelte";
    import { PRConnect } from "@projectroom/ui-lib";
    import { fade } from "svelte/transition";
    import { navigateTo } from "svelte-router-spa";
    import Preview from "./Preview.svelte";
    import DialogueScenario from "./DialogueScenario.svelte";
    import ContentsBlock from "./ContentsBlock.svelte";
    import HubOrgAdmin from "./hub/HubOrgAdmin.svelte";
    import HubBasic from "./hub/HubBasic.svelte";
    import HubUsage from "./hub/HubUsage.svelte";
    import BotImageAsset from "./BotImageAsset.svelte";
    import PageDesign from "./PageDesign.svelte";
    import StatisticsManagement from "./StatisticsManagement.svelte";
    import StatisticsDashboard from "./insights/StatisticsDashboard.svelte";
    import CollectionDashboard from "./insights/CollectionDashboard.svelte";
    import { messaging } from "./store/messaging-store";
    import { Color, Solver, hexToRgb } from "./util/SvgConvert.js";
    import Plan from "./../components/Plan.svelte";
    import { planStore } from "./store/plan-store";
    import BuilderSidebar from "./builder-shell/BuilderSidebar.svelte";
    import BuilderTopTabs from "./builder-shell/BuilderTopTabs.svelte";
    import { saveStatus, markSaving, markSaved } from "./store/builder-store.js";
    import { moveFolder, botAssetFolder } from "./chat-builder/imageAssetApi";

    export let currentRoute;
    export let params;

    // shadcn 셸 상태: 좁은 화면(≤1023px)에선 프리뷰를 오버레이 드로어로 전환
    let previewOpen = true;
    let isNarrow = false;
    const TYPE_2D = "2D";
    const TYPE_3D = "3D";
    const FLAG_Y = "Y";
    const FLAG_N = "N";
    const DEFAULT_COLOR = "";
    let planStoreSubsc;
    let builderMsgSubsc;

    class Bot {
        constructor(id, planStore) {
            this.id = id;
            this.bot2DChar = new BotChar({});
            this.bot3DChar = new BotChar({});
            this.botCharList = [];
            this.currentTab = "scenario";
            this.defaultChar = new BotChar({});
            this.planStore = planStore;
        }

        setPageData({
            backImageContent,
            backColor = "#2A364D",
            conversationTextColor = "#FFFFFF",
            conversationLineColor = "rgba(1,1,1,0)",
            lineColor = "#E9B2B2",
            conversationTextType,
            actionButtonCss,
            actionShadowFlag,
            conversationButtonCss,
            conversationShadowFlag,
            fixedConversationButtonCss,
            fixedConversationShadowFlag,
            shareButtonFlag,
            shareButtonCss,
            logoImageFlag,
            logoImageContent,
            backBlurFlag,
            backDarklyFlag,
            avatalkImageFlag,
            enterpriseLayoutType,
            coverImageContent,
        }) {
            this.backImageContent = backImageContent;
            this.backColor = backColor;
            this.lineColor = lineColor;
            this.conversationTextColor = conversationTextColor;
            this.conversationLineColor =
                conversationLineColor || "rgba(1,1,1,0)";
            this.conversationTextType = conversationTextType;
            this.actionButtonCss = new ActionButtonCss({
                ...JSON.parse(actionButtonCss),
                botId: this.id,
            });
            this.actionShadowFlag = actionShadowFlag;
            this.conversationButtonCss = new ConversationButtonCss({
                ...JSON.parse(conversationButtonCss),
                botId: this.id,
            });
            this.conversationShadowFlag = conversationShadowFlag;
            this.fixedConversationButtonCss = new ConversationButtonCss({
                ...JSON.parse(fixedConversationButtonCss),
                botId: this.id,
            });
            this.fixedConversationShadowFlag = fixedConversationShadowFlag;
            this.shareButtonFlag = shareButtonFlag;
            this.shareButtonCss = new ShareButtonCss({
                ...JSON.parse(shareButtonCss),
                botId: this.id,
            });
            this.logoImageFlag = logoImageFlag;
            this.logoImageContent = logoImageContent;
            this.backBlurFlag = backBlurFlag;
            this.backDarklyFlag = backDarklyFlag;
            this.avatalkImageFlag = avatalkImageFlag;
            this.enterpriseLayoutType = enterpriseLayoutType || 'TYPE_C';
            this.coverImageContent = coverImageContent;
        }

        setTitle({ title }) {
            this.title = title;
        }

        setDate({ lastUpdateDate, lastPostDate }) {
            this.lastUpdateDate = lastUpdateDate;
            this.lastPostDate = lastPostDate;
        }

        setData({ pageUrl, qrUrl, title, subTitle = "", iconImageContent = "", chatUrl = "", pageType = "2D" }) {
            this.pageUrl = pageUrl;
            this.qrUrl = qrUrl;
            this.title = title;
            this.subTitle = subTitle;
            this.iconImageContent = iconImageContent;
            this.chatUrl = chatUrl;
            this.pageType = pageType;
            messaging.set({
                target: "BuilderLayout",
                action: "dataLoad",
                bot: this,
            });
        }

        get isEnterprise() {
            return this.pageType === "ENTERPRISE";
        }

        /** 허브페이지(채팅을 격자로 모아 보여주는 진입 페이지) */
        get isHub() {
            return this.pageType === "HUB";
        }

        /**
         * React 셸로 렌더되는 타입인가(기업형·허브). 2D 캐릭터형과 달리
         * 시나리오/모션이 없어 데이터 로드·탭 구성이 같은 쪽으로 간다.
         * ⚠️ isEnterprise 와 구분해서 쓸 것 — 그건 '기업형 전용' 분기다.
         */
        get isShellPage() {
            return this.isEnterprise || this.isHub;
        }

        setScenarioList() {
            if (this.defaultChar.scenarioList.length > 0) {
                this.defaultChar.showActiveScenario();
                return;
            }
            const charId = this.defaultChar.charId;
            PRConnect.send(
                "/api/Bot/getScenarioListByCharId",
                { charId },
                (data) => {
                    if (data.header.status === "success") {
                        data.result.forEach((element, idx) => {
                            if (idx === 0) {
                                const active = true;
                                element = { ...element, active };
                            }
                            this.defaultChar.setScenario(element);
                        });
                    }
                    this.assign(false);
                },
            );
        }

        getScenarioListSize() {
            return this.defaultChar.scenarioList.length;
        }

        getCharActionListSize() {
            return this.defaultChar.charActionList.length;
        }

        getLogoImageContent() {
            return `https://groovworks-avatalk-public-file-storage.s3.ap-northeast-2.amazonaws.com/${this.id}/common/logo-image/${this.logoImageContent}`;
        }

        getBackImageContent() {
            return `https://groovworks-avatalk-public-file-storage.s3.ap-northeast-2.amazonaws.com/${this.id}/common/image/${this.backImageContent}`;
        }

        getCoverImageContent() {
            return `https://groovworks-avatalk-public-file-storage.s3.ap-northeast-2.amazonaws.com/${this.id}/common/cover-image/${this.coverImageContent}`;
        }

        isAuthority(target, context) {
            return this.planStore.isAuthority(target, context);
        }

        isDefaultCharType(type) {
            return (
                this.botChars
                    .find((e) => e.defaultFlag === FLAG_Y)
                    .getCharType() === type
            );
        }

        isLatestUpdated() {
            // 게시된적이 없으면 false
            if (!this.lastPostDate) {
                return false;
            }
            return new Date(this.lastPostDate) < new Date(this.lastUpdateDate);
        }

        isActionShadowFlag() {
            return this.actionShadowFlag === FLAG_Y;
        }

        isConversationShadowFlag() {
            return this.conversationShadowFlag === FLAG_Y;
        }

        isFixedConversationShadowFlag() {
            return this.fixedConversationShadowFlag === FLAG_Y;
        }
        isButtonBoxShadowFlag() {
            return this.actionShadowFlag === FLAG_Y;
        }

        getBotChar(type) {
            return this.botChars.find((e) => {
                e.defaultFlag;
            });
        }

        isCharActionComplted() {
            return (
                this.bot2DChar.charActionList.filter(
                    (element) =>
                        element.actionName.replace(" ", "").length === 0,
                ).length === 0
            );
        }
        isBackBlurFlag() {
            return this.backBlurFlag === FLAG_Y;
        }
        isBackDarklyFlag() {
            return this.backDarklyFlag === FLAG_Y;
        }
        isButtonRoundChecked(round) {
            return this.conversationButtonCss?.round === round;
        }
        isButtonBorderWidthChecked(borderWidth) {
            return this.conversationButtonCss?.borderWidth === borderWidth;
        }
        isConversationTextTypeChecked(textType) {
            return this.conversationTextType === textType;
        }
        isShareButtonFlag() {
            return this.shareButtonFlag === FLAG_Y;
        }

        isAvatalkImageFlag() {
            return this.avatalkImageFlag === FLAG_Y;
        }

        isAvailableType(target, context) {
            if (planStore.get() === undefined) {
                return false;
            }
            return planStore.isAvailableType(target, context);
        }

        isAvailableAuthority(target) {
            if (planStore.get() === undefined) {
                return false;
            }
            return planStore.isAvailableAuthority(target);
        }

        changeDefaultCharType(type) {
            if (type === TYPE_2D) {
                this.bot2DChar.changeDefaultFlag(FLAG_Y);
                this.bot3DChar.changeDefaultFlag(FLAG_N);
                this.defaultChar = this.bot2DChar;
            } else {
                this.bot2DChar.changeDefaultFlag(FLAG_N);
                this.bot3DChar.changeDefaultFlag(FLAG_Y);
                this.defaultChar = this.bot3DChar;
            }
        }

        isCurrentTab(tab) {
            return this.currentTab === tab;
        }

        changeCurrentTab(currentTab) {
            if (this.currentTab === currentTab) {
                return;
            }

            // if (!this.planStore.isAuthority("tabType", currentTab)) {
            // 	return;
            // }
            this.currentTab = currentTab;
            this.assign(false);
            jQuery(".builder-editor-scroll").animate({ scrollTop: 0 }, 100);
        }

        changeButtonRound(round) {
            this.actionButtonCss.setRound(round);
            this.conversationButtonCss.setRound(round);
            this.fixedConversationButtonCss.setRound(round);
            this.save();
        }
        changeButtonBorderWidth(borderWidth) {
            this.actionButtonCss.setBorderWidth(borderWidth);
            this.conversationButtonCss.setBorderWidth(borderWidth);
            this.fixedConversationButtonCss.setBorderWidth(borderWidth);
            this.save();
        }
        changeButtonShadowFlag() {
            this.actionShadowFlag =
                this.actionShadowFlag === FLAG_Y ? FLAG_N : FLAG_Y;
            this.conversationShadowFlag =
                !this.conversationShadowFlag === FLAG_Y ? FLAG_N : FLAG_Y;
            this.fixedConversationShadowFlag =
                !this.fixedConversationShadowFlag === FLAG_Y ? FLAG_N : FLAG_Y;
            this.save();
        }

        changeActionButtonCss(buttonCss) {
            this.actionButtonCss.setButtonCss(buttonCss);
            this.save();
        }
        changeConversationButtonCss(conversationButtonCss) {
            this.conversationButtonCss.setButtonCss(conversationButtonCss);
            this.save();
        }
        changeFixedConversationButtonCss(fixedConversationButtonCss) {
            this.fixedConversationButtonCss.setButtonCss(
                fixedConversationButtonCss,
            );
            this.save();
        }

        // 허브 포인트 색 — 진행 막대·아이콘 상자·완료 체크(서버가 custom.css 에 --hub-point 로 굽는다)
        changeHubAccentColor(color) {
            this.fixedConversationButtonCss.setAccentColor(color);
            this.save();
        }

        // 허브 테마 — 배경색·카드 색·포인트 색을 한 번에 바꾸고 **한 번** 저장한다. 프리셋은 화면(PageDesign HUB_THEMES)이 갖는다.
        // 저장하는 칸은 전부 기존 디자인 값이다(새 컬럼 없음) — 고른 뒤 아래 색 칸에서 하나씩 손볼 수 있다.
        applyHubTheme({ backColor, block, accentColor }) {
            this.backColor = backColor;
            this.fixedConversationButtonCss.setButtonCss({ ...block, boxShadow: this.fixedConversationButtonCss.boxShadow });
            this.fixedConversationButtonCss.setAccentColor(accentColor);
            this.save();
        }

        changeShadowFlag(shadowType, flag) {
            this[shadowType] = flag;
        }

        changeShareButtonCss(shareButtonCss) {
            this.shareButtonCss.setCss(shareButtonCss);
            this.save();
        }

        changeConversationTextColor(color) {
            this.conversationTextColor = color;
            this.save();
        }

        changeConversationLineColor(color) {
            this.conversationLineColor = color;
            this.save();
        }

        changeConversationTextType(type) {
            this.conversationTextType = type;
            this.save();
        }

        changeShareButtonFlag() {
            this.shareButtonFlag =
                this.shareButtonFlag === FLAG_Y ? FLAG_N : FLAG_Y;
            this.save();
        }

        changeAvatalkImageFlag() {
            this.avatalkImageFlag =
                this.avatalkImageFlag === FLAG_Y ? FLAG_N : FLAG_Y;
            this.save();
        }

        changeBackImageContent(content) {
            this.backImageContent = content;
            this.save();
        }

        changeBackColor(color) {
            this.backColor = color;
            this.save();
        }

        changeBackBlurFlag() {
            this.backBlurFlag = this.backBlurFlag === FLAG_Y ? FLAG_N : FLAG_Y;
            this.save();
        }

        changeBackDarklyFlag(flag) {
            this.backDarklyFlag =
                this.backDarklyFlag === FLAG_Y ? FLAG_N : FLAG_Y;
            this.save();
        }

        changeLineColor(lineColor) {
            this.lineColor = lineColor.toRgbString();
            this.watermarkColor = this.convertSvgFilter(lineColor.toHex());
            this.shareButtonCss.setCss({ color: lineColor.toRgbString() });
            this.save();
        }

        changeLogoImageFlag(flag) {
            this.logoImageFlag = flag;
            this.save();
        }

        changeLogoImageContent(content) {
            this.logoImageContent = content;
            this.save();
        }

        changeCoverImageContent(content) {
            this.coverImageContent = content;
            this.save();
        }

        changeEnterpriseLayoutType(type) {
            this.enterpriseLayoutType = type;
            this.save();
            bot.assign();
        }

        changeBotTitle(title) {
            if (this.title === title) return;
            const oldTitle = this.title;
            this.title = title;
            markSaving();
            PRConnect.send(
                "/api/Bot/modifyBot",
                { id: this.id, title, subTitle: this.subTitle, iconImageContent: this.iconImageContent },
                (data) => {
                    if (data.header.status === "success") {
                        bot.applySaved(data);
                        // 제목이 바뀌면 이 봇 문맥 폴더(봇/{제목}) 하위 자산 전체를 새 제목 폴더로 이동(프리픽스 일괄 치환).
                        moveFolder(
                            null,
                            botAssetFolder({ title: oldTitle, id: this.id, pageType: this.pageType }),
                            botAssetFolder(this),
                        ).catch(() => {});
                    }
                    bot.assign(false);
                },
            );
        }

        changeBotSubTitle(subTitle) {
            if (this.subTitle === subTitle) return;
            this.subTitle = subTitle;
            markSaving();
            PRConnect.send(
                "/api/Bot/modifyBot",
                { id: this.id, title: this.title, subTitle, iconImageContent: this.iconImageContent },
                (data) => {
                    if (data.header.status === "success") {
                        bot.applySaved(data);
                    }
                    bot.assign(false);
                },
            );
        }

        changeChatUrl(chatUrl) {
            if (this.chatUrl === chatUrl) {
                return;
            }
            this.chatUrl = chatUrl;
            this.saveBotForPublish();
        }

        assign(action = true) {
            bot = bot;
        }

        convertSvgFilter(lineColor) {
            //#C9C9C9 값을 svg에 적용한 값
            let result =
                "invert(92%) sepia(3%) saturate(8%) hue-rotate(326deg) brightness(87%) contrast(94%)";
            try {
                const rgb = hexToRgb(lineColor);
                if (rgb.length !== 3) {
                    return result;
                }
                const color = new Color(rgb[0], rgb[1], rgb[2]);
                const solver = new Solver(color);
                result = solver.solve();
            } catch {
                return result;
            }
            return result.filter;
        }

        save() {
            const param = {
                ...this,
                actionButtonCss: JSON.stringify(this.actionButtonCss),
                conversationButtonCss: JSON.stringify(
                    this.conversationButtonCss,
                ),
                fixedConversationButtonCss: JSON.stringify(
                    this.fixedConversationButtonCss,
                ),
                shareButtonCss: JSON.stringify(this.shareButtonCss),
            };
            markSaving();
            PRConnect.send("/api/Bot/modifyBotDesign", param, (data) => {
                if (data.header.status === "success") {
                    console.log("BotPageData update success");
                    bot.applySaved(data);
                }
                bot.assign(false);
                messaging.set({
                    target: "Preview",
                    action: "refreshPreview",
                    botId: this.id,
                    charId: this.defaultChar?.charId,
                });
            });
            bot.assign(false);
        }

        saveBotForPublish() {
            PRConnect.send(
                "/api/Bot/modifyChatUrl",
                { ...this, charId: this.defaultChar.charId },
                (data) => {
                    if (data.header.status === "success") {
                        bot.assign(false);
                        messaging.set({
                            target: "Preview",
                            action: "refreshPreview",
                            botId: this.id,
                            charId: this.defaultChar?.charId,
                        });
                        messaging.set({
                            target: "BuilderLayout",
                            action: "getLastUpdateDate",
                            bot,
                        });
                    }
                },
            );
        }

        applySaved(data) {
            // 저장 완료 처리(2차 호출 없음). 모든 저장 경로가 성공 시 이 메서드를 호출한다.
            // 예전엔 여기서 /api/Bot/modifyBotUpdateDate를 한 번 더 호출(라운드트립 2회)했지만,
            // 이제 서버가 각 저장 응답 header에 최신 lastUpdateDate를 실어보내므로 그 값만으로
            // 자동저장 pill(상단 툴바)과 마지막수정일을 갱신한다.
            markSaved();
            const meta = data?.header;
            if (meta?.lastUpdateDate) {
                bot.setDate({
                    lastUpdateDate: meta.lastUpdateDate,
                    lastPostDate: meta.lastPostDate,
                });
                messaging.set({
                    target: "BuilderLayout",
                    action: "getLastUpdateDate",
                    bot,
                });
            }
        }
    }

    class ActionButtonCss {
        constructor({
            botId,
            color = "#FFFFFF",
            lineColor = "#94A7C6",
            mouseOverColor = "#EBF2FF",
            mouseOverLineColor = "#5A7DB7",
            fontColor = "#000000",
            round = 20,
            borderWidth = 0,
            boxShadow,
        }) {
            this.botId = botId;
            this.color = color;
            this.lineColor = lineColor;
            this.mouseOverColor = mouseOverColor;
            this.mouseOverLineColor = mouseOverLineColor;
            this.fontColor = fontColor;
            this.round = round;
            this.borderWidth = borderWidth;
            this.boxShadow = boxShadow;
        }
        setButtonCss({
            color = "#FFFFFF",
            lineColor = "#94A7C6",
            mouseOverColor = "#EBF2FF",
            mouseOverLineColor = "#5A7DB7",
            fontColor = "#000000",
            boxShadow,
        }) {
            this.color = color;
            this.lineColor = lineColor;
            this.mouseOverColor = mouseOverColor;
            this.mouseOverLineColor = mouseOverLineColor;
            this.fontColor = fontColor;
            this.boxShadow = boxShadow;
        }
        setRound(round) {
            this.round = round;
        }
        setBorderWidth(borderWidth) {
            this.borderWidth = borderWidth;
        }
    }

    class ConversationButtonCss {
        constructor({
            botId,
            color = "#23375C",
            lineColor = "#23375C",
            mouseOverColor = "#102242",
            mouseOverLineColor = "#102242",
            fontColor = "#FFFFFF",
            round,
            borderWidth = 0,
            boxShadow,
            // 허브 포인트 색(진행 막대·아이콘 상자·완료 체크). 새 컬럼 대신 이 JSON 의 한 칸이다(round·borderWidth 처럼).
            // ⚠️ 여기서 받지 않으면 로드할 때 떨어지고, 다음 저장(JSON.stringify(this))이 DB 에서도 지운다.
            accentColor = null,
        }) {
            this.botId = botId;
            this.color = color;
            this.lineColor = lineColor;
            this.mouseOverColor = mouseOverColor;
            this.mouseOverLineColor = mouseOverLineColor;
            this.fontColor = fontColor;
            this.round = round;
            this.borderWidth = borderWidth;
            this.boxShadow = boxShadow;
            this.accentColor = accentColor;
        }

        // setButtonCss 와 따로 둔다 — 색 다섯 개를 다시 고를 때(PageDesign 콘텐츠 버튼 컬러) 포인트 색이 지워지지 않게
        setAccentColor(accentColor) {
            this.accentColor = accentColor || null;
        }

        setButtonCss({
            color = "#23375C",
            lineColor = "#23375C",
            mouseOverColor = "#102242",
            mouseOverLineColor = "#102242",
            fontColor = "#FFFFFF",
            boxShadow,
        }) {
            this.color = color;
            this.lineColor = lineColor;
            this.mouseOverColor = mouseOverColor;
            this.mouseOverLineColor = mouseOverLineColor;
            this.fontColor = fontColor;
            this.boxShadow = boxShadow;
        }

        setRound(round) {
            this.round = round;
        }
        setBorderWidth(borderWidth) {
            this.borderWidth = borderWidth;
        }
    }

    class ShareButtonCss {
        constructor({ botId, color = "#6372FF", round = 20 }) {
            this.botId = botId;
            this.color = color;
            this.round = round;
        }
        setCss({ color, round = 20 }) {
            this.color = color;
            this.round = round;
        }
        setRound(round) {
            this.round;
        }
    }

    class BotChar {
        constructor({
            id,
            botId,
            charId,
            charType,
            defaultFlag,
            gltfContent,
            charSize,
            charPosX,
            charPosY,
            charPosZ,
            charDirectIntensity,
            charDirectColor,
            charAmbientIntensity,
            charAmbientColor,
            rightButtonContext,
        }) {
            this.id = id;
            this.botId = botId;
            this.charId = charId;
            this.charType = charType;
            this.defaultFlag = defaultFlag;
            this.gltfContent = gltfContent;
            this.charSize = charSize;
            this.charPosX = charPosX;
            this.charPosY = charPosY;
            this.charPosZ = charPosZ;
            this.charDirectIntensity = charDirectIntensity;
            this.charDirectColor = charDirectColor;
            this.charAmbientIntensity = charAmbientIntensity;
            this.charAmbientColor = charAmbientColor;
            this.charActionList = [];
            this.scenarioList = [];
            this.fixedButtonList = [];
            this.rightButtonList = [];
            this.activeScenario = new Scenario({});
        }
        isCharType(type) {
            return this.charType === type;
        }

        isDefaultFlag() {
            return this.defaultFlag === FLAG_Y;
        }

        addCharAction(imageOrgContent = "", scenario) {
            let actionName = "동작이름" + this.charActionList.length;
            const charAction = new CharAction({
                ...this,
                actionName,
                imageOrgContent,
                addFlag: "N",
            });
            this.charActionList.push(charAction);
            markSaving();
            PRConnect.send(
                "/api/Bot/createBotCharAction",
                { ...charAction },
                (data) => {
                    if (data.header.status === "success") {
                        charAction.id = data.result;
                        console.log("charAction add success");
                        bot.applySaved(data);
                        if (scenario) {
                            scenario.changeCharAction(charAction.id);
                        }
                    }
                    bot.assign(false);
                },
            );
            bot.assign(false);
        }

        changeCharActionImageOrgContent(index, imageOrgContent) {
            this.charActionList[index].changeImageOrgContent(imageOrgContent);
        }

        /**
         *    button은 시나리오 대화버튼에서 대화 시나리오 추가할 때
         *    시나리오 추가하면서 해당 시나리오를 기존 대화버튼에 연결하기 위함
         **/
        addScenario(button) {
            const seq = this.scenarioList[this.scenarioList.length - 1].seq + 1;
            const title = "섹션" + seq;
            const botId = bot.defaultChar.botId;
            const scenario = new Scenario({ ...this, seq, title, botId });
            this.scenarioList.push(scenario);
            markSaving();
            PRConnect.send(
                "/api/Bot/createScenario",
                { ...scenario },
                (data) => {
                    if (data.header.status === "success") {
                        let scenarioId = data.result.scenarioId;
                        let buttonId = data.result.buttonId;
                        scenario.id = scenarioId;
                        if (button) {
                            button.changeTargetScenario(scenarioId);
                        }
                        this.setActiveScenario(scenarioId);
                        // 서버 로직 상 시나리오 1개에 버튼 1개가 만들어짐. 해당 부분 화면단에도 싱크 맞추는 작업
                        const initButton = new Button({
                            id: buttonId,
                            scenarioId,
                            seq: 1,
                            botId,
                            type: "DEFAULT",
                        });
                        scenario.setButton(initButton);
                        console.log("scenario add success");
                        bot.applySaved(data);
                    }
                    bot.assign(false);
                },
            );
            bot.assign(false);
        }

        setScenario(param) {
            let scenario = new Scenario(param);
            if (param.active) {
                this.activeScenario = scenario;
                // scenario.setButtonList();
                // scenario.setActive(param.active);
            }
            this.scenarioList.push(scenario);
            return scenario;
        }

        setActiveScenario(id) {
            if (this.activeScenario.id == id) {
                return;
            }
            this.scenarioList.forEach((e) => e.setActive(false));
            const scenario = this.scenarioList.find((e) => e.id == id);
            scenario.setActive(true);
            bot.assign(false);
            this.activeScenario = scenario;
            messaging.set({
                target: "Preview",
                action: "refreshPreview",
                botId: this.botId,
                charId: this.charId,
                scenarioId: this.id,
            });
        }

        setActiveButton(button) {
            if (document.activeElement) document.activeElement.blur();
            this.rightButtonList.forEach((e) => e.setActive(false));
            this.fixedButtonList.forEach((e) => e.setActive(false));
            button.setActive(true);
            this.scenarioList.forEach((e) => e.setActive(false));
            bot.assign(false);
            // preview iframe 안의 대응 블럭에 하이라이트 애니메이션 요청
            messaging.set({
                target: "Preview",
                action: "highlightBlock",
                botId: this.botId,
                buttonId: button.id,
            });
        }

        // _getCharAction(charId, callback ) {
        //     this.charActionList = [];
        //     PRConnect.send("/api/Bot/getBotCharActionListByCharId", {charId}, data => {
        //         if( data.header.status === "success") {
        //             data.result.forEach(element => {
        //                 this.charActionList.push(new CharAction(element));
        //             });
        //             bot.assign(false);

        //             if( callback ) {
        //                 callback.call(this, this.botId, this.charId);
        //             }
        //         }
        //     });
        // }

        // refreshCharAction( callback ){
        //     this._getCharAction(this.charId, callback);
        // }

        setCharActionList(charActionList) {
            charActionList.forEach((element) => {
                this.charActionList.push(new CharAction(element));
            });
        }

        setFixedButtonList(buttonList) {
            buttonList.forEach((element) => {
                if (element.type === "FIX") {
                    this.fixedButtonList.push(new Button(element));
                }
            });
        }

        setRightButtonList(buttonList) {
            buttonList.forEach((element) => {
                if (element.type === "RIGHT") {
                    this.rightButtonList.push(new Button(element));
                }
            });
        }

        addFixedButton(targetType) {
            let seq =
                this.fixedButtonList.length > 0
                    ? this.fixedButtonList[this.fixedButtonList.length - 1]
                          .seq + 1
                    : 1;
            const button = new Button({
                seq,
                botId: this.botId,
                type: "FIX",
                targetType,
            });
            this.fixedButtonList.push(button);
            markSaving();
            PRConnect.send("/api/Bot/createButton", { ...button }, (data) => {
                if (data.header.status === "success") {
                    button.id = data.result;
                    bot.applySaved(data);
                }
                bot.assign(false);
            });
            bot.assign(false);
        }

        // 콘텐츠 블록을 '타입을 고른 상태'로 바로 추가한다.
        // (기존엔 빈 블록 추가 → 블록 안 팔레트에서 타입 선택하는 2단계였음)
        // 타입을 처음부터 지정해 팔레트 깜빡임 없이 바로 해당 편집기로 렌더하고,
        // 생성 후 save()로 타입을 확정 저장한다(서버 create가 targetType 미반영이어도 안전).
        addContentBlock(targetType) {
            let seq =
                this.fixedButtonList.length > 0
                    ? this.fixedButtonList[this.fixedButtonList.length - 1]
                          .seq + 1
                    : 1;
            const button = new Button({
                seq,
                botId: this.botId,
                type: "FIX",
                targetType,
            });
            // 개인정보 입력폼은 최소 1개 항목이 필요 → 기본값(NAME) 시드
            if (targetType === "PRIVACY" && !button.targetLink) {
                button.targetLink = "NAME";
            }
            this.fixedButtonList.push(button);
            markSaving();
            PRConnect.send("/api/Bot/createButton", { ...button }, (data) => {
                if (data.header.status === "success") {
                    button.id = data.result;
                    button.save(); // 생성된 id로 targetType/targetLink 확정 저장
                    bot.applySaved(data);
                }
                bot.assign(false);
            });
            bot.assign(false);
        }

        addRightButton(targetType) {
            let seq =
                this.rightButtonList.length > 0
                    ? this.rightButtonList[this.rightButtonList.length - 1]
                          .seq + 1
                    : 1;
            const button = new Button({
                seq,
                botId: this.botId,
                type: "RIGHT",
                targetType,
            });
            this.rightButtonList.push(button);
            markSaving();
            PRConnect.send("/api/Bot/createButton", { ...button }, (data) => {
                if (data.header.status === "success") {
                    button.id = data.result;
                    bot.applySaved(data);
                }
                bot.assign(false);
            });
            bot.assign(false);
        }

        getCharAction(charActionId) {
            return this.charActionList.find((e) => e.id === charActionId);
        }

        getActiveScenario() {
            return this.scenarioList.find((element) => element.active);
        }

        getScenario(scenarioId) {
            return this.scenarioList.find((e) => e.id === scenarioId);
        }

        showActiveScenario() {
            this.getActiveScenario().setActive(true);
        }

        setimageOrgContent(index, content) {
            this.charActionList[index].imageOrgContent = content;
        }

        changeFixedButtonSeq(id, idx) {
            this.fixedButtonList.find((e) => e.id === id).changeSeq(idx + 1);
        }

        changeRightButtonSeq(id, idx) {
            this.rightButtonList.find((e) => e.id === id).changeSeq(idx + 1);
        }

        changeActiveScenarioToStartType() {
            let scenarioId = this.scenarioList.find((e) => e.type === "START");
            this.setActiveScenario(scenarioId);
        }

        changeBotCharField(field, value) {
            this[field] = value;
            this.save();
        }

        changeCharActionActionName(index, actionName) {
            this.charActionList[index].changeActionName(actionName);
        }

        changeCharActionAddFlag(index, addFlag) {
            const addFlagYCount = this.charActionList.filter(
                (e) => e.addFlag === FLAG_Y,
            ).length;
            if (addFlagYCount > 5) {
                return;
            }
            if (addFlagYCount === 5 && addFlag === FLAG_Y) {
                return;
            }
            this.charActionList[index].changeAddFlag(this.gltfContent);
        }

        changeCharActionWaitingFlag(index) {
            this.charActionList.forEach((e) => e.setWaitingFlag(false));
            this.charActionList[index].changeWaitingFlag(this.gltfContent);
        }

        changeDefaultFlag(defaultFlag) {
            this.defaultFlag = defaultFlag;
            this.save();
        }

        changeGltfContent(gltfContent, saveFlag) {
            this.gltfContent = gltfContent;
            if (saveFlag) {
                this.save();
            } else {
                bot.assign(false);
            }
        }

        deleteCharActionImage(index) {
            this.charActionList[index].deleteImageContent();
        }

        deleteGltfContent() {
            this.gltfContent = undefined;
            this.save();
        }

        deleteScenario(scenario) {
            this.scenarioList = this.scenarioList.filter(
                (e) => e.id !== scenario.id,
            );
            this.scenarioList.forEach((e) =>
                e.buttonList.forEach((button) =>
                    button.changeTargetScenarioByDelete(scenario.id),
                ),
            );
            this.fixedButtonList.forEach((button) =>
                button.changeTargetScenarioByDelete(scenario.id),
            );
            scenario.delete();
        }

        deleteFixedButton(button) {
            this.fixedButtonList = this.fixedButtonList.filter(
                (e) => e.id !== button.id,
            );
            button.delete();
        }

        deleteRightButton(button) {
            this.rightButtonList = this.rightButtonList.filter(
                (e) => e.id !== button.id,
            );
            button.delete();
        }

        deleteCharAction(charAction) {
            this.charActionList = this.charActionList.filter(
                (e) => e.id !== charAction.id,
            );
            this.scenarioList.forEach((scenario) =>
                scenario.deleteCharActionForSync(charAction.id),
            );
            charAction.delete();
        }

        save() {
            markSaving();
            PRConnect.send("/api/Bot/modifyBotChar", { ...this }, (data) => {
                if (data.header.status === "success") {
                    console.log("botChar update success");
                    bot.applySaved(data);
                }
                bot.assign(false);
                messaging.set({
                    target: "Preview",
                    action: "refreshPreview",
                    botId: this.botId,
                    charId: this.charId,
                });
            });
            bot.assign(false);
        }
    }

    class CharAction {
        constructor({
            id,
            charId,
            botId,
            imageOrgContent,
            imageContent,
            imageCss = '{"width":"100%"}',
            actionName = "",
            addFlag,
            waitingFlag = "N",
        }) {
            this.id = id;
            this.charId = charId;
            this.botId = botId;
            this.imageOrgContent = imageOrgContent;
            this.imageContent = imageContent;
            this.imageCss = imageCss;
            this.actionName = actionName;
            this.addFlag = addFlag;
            this.waitingFlag = waitingFlag ?? "N";
        }

        setWaitingFlag(waitingFlag) {
            this.waitingFlag = waitingFlag;
        }

        save() {
            markSaving();
            PRConnect.send(
                "/api/Bot/modifyBotCharAction",
                {
                    ...this,
                    logoImageFlag: bot.logoImageFlag,
                    logoImageContent: bot.logoImageContent,
                },
                (data) => {
                    if (data.header.status === "success") {
                        console.log("charAction update success");
                        bot.applySaved(data);
                    }
                    bot.assign(false);
                    messaging.set({
                        target: "Preview",
                        action: "refreshPreview",
                        botId: this.botId,
                        charId: this.charId,
                        gltf: this.gltfContent,
                    });
                },
            );
            bot.assign(false);
        }

        isAddFlag() {
            return this.addFlag === FLAG_Y;
        }

        changeActionName(actionName) {
            this.actionName = actionName;
            this.save();
        }

        changeAddFlag(gltfContent) {
            this.gltfContent = gltfContent;
            this.addFlag = this.addFlag === FLAG_Y ? FLAG_N : FLAG_Y;
            this.save();
        }
        changeWaitingFlag(gltfContent) {
            this.gltfContent = gltfContent;
            this.waitingFlag = this.waitingFlag === FLAG_Y ? FLAG_N : FLAG_Y;
            this.save();
        }

        changeImageContent(imageContent) {
            this.imageContent = imageContent;
            this.save();
        }

        changeImageOrgContent(imageOrgContent) {
            this.imageOrgContent = imageOrgContent;
            this.save();
        }
        changeImageCss(imageCss) {
            this.imageCss = JSON.stringify(imageCss);
            this.save();
        }

        getImageUrl() {
            return `https://groovworks-avatalk-public-file-storage.s3.ap-northeast-2.amazonaws.com/${this.botId}/${this.charId}/bot-images/${this.imageOrgContent}`;
        }

        getImageCss() {
            let result = "";
            const imageCss = JSON.parse(this.imageCss);
            for (const key of Object.keys(JSON.parse(this.imageCss))) {
                result += `${key}:${imageCss[key]};`;
            }
            return result;
        }

        delete() {
            markSaving();
            PRConnect.send(
                "/api/Bot/deleteBotCharAction",
                { id: this.id, botId: this.botId },
                (data) => {
                    if (data.header.status === "success") {
                        bot.applySaved(data);
                    }
                    bot.assign(false);
                },
            );
        }

        deleteImageContent() {
            this.imageContent = undefined;
            this.imageOrgContent = undefined;
            markSaving();
            PRConnect.send(
                "/api/Bot/modifyBotCharAction",
                { ...this },
                (data) => {
                    if (data.header.status === "success") {
                        console.log("charAction delete image success");
                        bot.applySaved(data);
                    }
                    bot.assign(false);
                },
            );
            bot.assign(false);
        }
    }

    class Scenario {
        constructor({
            id,
            botId,
            charId,
            title,
            seq,
            type = "NORMAL",
            charMent = "",
            charAction,
            active = false,
            backColor = "#AAAAAA",
            backColorFlag = "N",
            rightButtonContext,
        }) {
            this.id = id;
            this.botId = botId;
            this.charId = charId;
            this.title = title;
            this.seq = seq;
            this.type = type;
            this.charMent = charMent == null ? "" : charMent;
            this.charAction = charAction;
            this.active = active;
            this.backColor = backColor =
                backColor == null ? "#FFFFFF" : backColor;
            this.buttonList = [];
            this.fixedButtonList = [];
            this.backColorFlag = backColorFlag;
        }

        isValidJson(jsonString) {
            try {
                JSON.parse(jsonString);
                return true;
            } catch (e) {
                return false;
            }
        }

        save() {
            markSaving();
            PRConnect.send("/api/Bot/modifyScenario", { ...this }, (data) => {
                if (data.header.status === "success") {
                    console.log("scenario update success");
                    bot.applySaved(data);
                }
                bot.assign(false);
                messaging.set({
                    target: "Preview",
                    action: "refreshPreview",
                    botId: this.botId,
                    charId: this.charId,
                    scenarioId: this.id,
                });
            });
            bot.assign(false);
        }

        setActive(active) {
            if (active) {
                messaging.set({
                    target: "Preview",
                    action: "refreshPreview",
                    botId: this.botId,
                    charId: this.charId,
                    scenarioId: this.id,
                });
            }
            this.active = active;
        }

        setButtonList(buttonList) {
            buttonList.forEach((element, idx) => {
                if (this.id == element.scenarioId) {
                    if (element.type === "DEFAULT") {
                        this.buttonList.push(new Button(element));
                    }
                }
            });
        }

        isStart() {
            return this.type === "START";
        }

        isUsableBackColor() {
            return this.backColorFlag === FLAG_Y;
        }

        toggleBackColor() {
            this.backColorFlag =
                this.backColorFlag === FLAG_Y ? FLAG_N : FLAG_Y;
            if (this.backColorFlag === FLAG_N) {
                this.backColor = "rgb(0, 0, 0)";
            }
            this.save();
        }

        getCharMentRows() {
            return this.charMent.split("\n").length;
        }

        // 첫 시나리오가 만들어질때 서버에서 만들어진 버튼
        setButton(button) {
            this.buttonList.push(button);
            bot.assign();
        }

        addButton(type, targetType) {
            let seq =
                this.buttonList.length > 0
                    ? this.buttonList[this.buttonList.length - 1].seq + 1
                    : 1;
            if (type == "FIX") {
                seq =
                    this.fixedButtonList.length > 0
                        ? this.fixedButtonList[this.fixedButtonList.length - 1]
                              .seq + 1
                        : 1;
            }
            const button = new Button({
                scenarioId: this.id,
                seq,
                botId: this.botId,
                type,
                targetType,
            });
            if (type == "FIX") {
                this.fixedButtonList.push(button);
            } else {
                this.buttonList.push(button);
            }
            markSaving();
            PRConnect.send("/api/Bot/createButton", { ...button }, (data) => {
                if (data.header.status === "success") {
                    button.id = data.result;
                    bot.applySaved(data);
                }
                bot.assign(false);
                messaging.set({
                    target: "Preview",
                    action: "refreshPreview",
                    botId: this.botId,
                    charId: this.charId,
                    scenarioId: this.id,
                });
            });
            bot.assign(false);
        }

        deleteButton(index, type) {
            let button = this.buttonList[index];
            if (type == "FIX") {
                button = this.fixedButtonList[index];
                this.fixedButtonList = this.fixedButtonList.filter(
                    (e, idx) => idx != index,
                );
            } else {
                this.buttonList = this.buttonList.filter(
                    (e, idx) => idx != index,
                );
            }
            button.delete();
        }

        deleteCharActionForSync(charActionId) {
            if (this.charAction === charActionId) {
                this.charAction = null;
            }
        }

        deleteButtonImage(buttonId) {
            let button = this.findButtonByButtonId(buttonId);
            button.deleteImage();
        }

        changeBackColor(color) {
            this.backColor = color;
            this.save();
        }

        changeCharMent(charMent) {
            // 무분별한 이벤트 발생 막기위함
            if (this.charMent === charMent) {
                return;
            }
            this.charMent = charMent;
            this.save();
        }

        changeTitle(title) {
            if (this.title === title) {
                return;
            }
            this.title = title;
            this.save();
        }

        changeCharAction(charAction) {
            if (this.charAction === charAction) {
                return false;
            }
            this.charAction = charAction;
            this.save();
        }

        changeButtonTitle(title, index, type) {
            let button;
            if (type == "FIX") {
                button = this.fixedButtonList[index];
            } else {
                button = this.buttonList[index];
            }
            button.changeTitle(title);
        }

        changeButtonTargetType(targetType, index, type) {
            let button;
            if (type == "FIX") {
                button = this.fixedButtonList[index];
            } else {
                button = this.buttonList[index];
            }
            button.changeTargetType(targetType);
        }

        changeButtonImage(buttonId, imageContent, imageType, func) {
            let button = this.findButtonByButtonId(buttonId);
            button.changeImage(imageContent, imageType, func);
        }

        changeButtonImageType(buttonId, imageType) {
            let button = this.findButtonByButtonId(buttonId);
            button.changeImageType(imageType);
        }

        changeTargetScenario(scenarioId, index, type) {
            let button;
            if (type == "FIX") {
                button = this.fixedButtonList[index];
            } else {
                button = this.buttonList[index];
            }
            button.changeTargetScenario(scenarioId);
        }

        changeButtonTargetLink(targetLink, index, buttonType, linkType) {
            let button;
            if (buttonType == "FIX") {
                button = this.fixedButtonList[index];
            } else {
                button = this.buttonList[index];
            }
            button.changeTargetLink(targetLink, linkType);
        }

        changeButtonSeq(id, idx, type) {
            if (type == "DEFAULT") {
                this.buttonList.find((e) => e.id === id).changeSeq(idx + 1);
            }
        }
        findButtonByButtonId(buttonId) {
            let button;
            button = this.fixedButtonList.filter(
                (button) => button.id == buttonId,
            )[0];
            if (!button) {
                button = this.buttonList.filter(
                    (button) => button.id == buttonId,
                )[0];
            }
            return button;
        }

        deleteCharAction() {
            this.charAction = null;
            this.save();
        }

        delete() {
            markSaving();
            PRConnect.send(
                "/api/Bot/deleteScenario",
                { id: this.id, botId: this.botId },
                (data) => {
                    if (data.header.status === "success") {
                        bot.applySaved(data);
                    }
                    bot.assign(false);
                },
            );
        }
    }

    class Button {
        hideFlag = false;
        active = false;

        constructor({
            id,
            scenarioId,
            botId,
            seq,
            title = "",
            subTitle = "",
            targetType,
            targetScenarioId,
            targetLink = "",
            type,
            buttonImageContent,
            buttonImageType,
            buttonImageAssetId,
        }) {
            this.id = id;
            this.scenarioId = scenarioId;
            this.botId = botId;
            this.seq = seq;
            this.title = title;
            this.subTitle = subTitle;
            this.targetType = targetType;
            this.targetScenarioId = targetScenarioId;
            this.targetLink = targetLink;
            this.type = type;
            this.buttonImageContent = buttonImageContent;
            this.buttonImageType = buttonImageType;
            // 통합 이미지 자산 ref. 있으면 서버가 현재버전 URL로 resolve(교체 자동전파). 없으면 buttonImageContent(레거시).
            this.buttonImageAssetId = buttonImageAssetId ?? null;
        }

        setActive(flag) {
            this.active = flag;
        }

        isActive() {
            return this.active;
        }

        changeTitle(title) {
            if ((this.title || '') === (title || '')) {
                return;
            }
            this.title = title;
            this.save();
        }
        changeSubTitle(subTitle) {
            if ((this.subTitle || '') === (subTitle || '')) {
                return;
            }
            this.subTitle = subTitle;
            this.save();
        }
        changeTargetType(targetType) {
            if (this.targetType === targetType) {
                return false;
            }
            this.targetType = targetType;
            this.save();
        }
        changeTargetScenario(targetScenarioId, targetType = "SCENARIO") {
            if (this.targetScenarioId === targetScenarioId) {
                return false;
            }
            this.targetScenarioId = targetScenarioId;
            this.targetType = targetType;
            this.save();
        }
        // 시나리오 삭제로 인해 해당 시나리오를 타겟시나리오로 설정하고 있는 버튼 값 변경
        // 서버에선 update되었고 싱크를 맞추기 위한 작업이라 추가 API 호출을 하지 않기 위한 메소드
        changeTargetScenarioByDelete(targetScenarioId) {
            this.targetScenarioId =
                this.targetScenarioId === targetScenarioId
                    ? null
                    : this.targetScenarioId;
        }
        changeTargetLink(targetLink, targetType) {
            if (
                (this.targetLink || '') === (targetLink || '') &&
                this.targetType == targetType
            ) {
                return;
            }
            this.targetLink = targetLink;
            this.targetType = targetType;
            this.save();
        }
        changeImage(buttonImageContent, buttonImageType, func) {
            this.buttonImageContent = buttonImageContent;
            this.buttonImageType = buttonImageType;
            this.buttonImageAssetId = null; // 레거시 업로드/빌트인 아이콘 → 자산 참조 해제
            this.save(func);
        }
        // 통합 라이브러리(ImagePicker)에서 고른 자산 지정: assetId(ref) + url(미리보기/듀얼모드 폴백) 동시 저장.
        // 서버는 buttonImageAssetId로 현재버전 URL을 resolve하므로 교체 시 자동 전파된다.
        changeImageAsset(assetId, url, buttonImageType = "STACKED") {
            this.buttonImageAssetId = assetId;
            this.buttonImageContent = url;
            this.buttonImageType = buttonImageType;
            this.save();
        }
        changeImageType(buttonImageType) {
            if (this.buttonImageType === buttonImageType) {
                return;
            }
            this.buttonImageType = buttonImageType;
            this.save();
        }
        changeSeq(seq) {
            if (this.seq === seq) {
                return;
            }
            this.seq = seq;
            this.save();
        }
        changeTargetTypeForPrivacy() {
            this.targetType = "PRIVACY";
            // 개인정보 항목은 최소 1개가 필요하므로 NAME을 초기 선택값으로 둔다(이후 다른 항목으로 교체 가능).
            if (!this.targetLink) {
                this.targetLink = "NAME";
            }
            this.save();
        }

        changePrivacyContext(context) {
            if (!context) {
                this.save();
                return;
            }
            let privacyArray = this.targetLink
                ? this.targetLink.split(",").filter(Boolean)
                : [];
            if (privacyArray.includes(context)) {
                // 최소 1개는 선택되어 있어야 하므로 마지막 항목 해제는 막는다
                if (privacyArray.length <= 1) {
                    return;
                }
                privacyArray = privacyArray.filter((e) => e !== context);
            } else {
                privacyArray.push(context);
            }
            this.targetLink = privacyArray.join(",");
            this.save();
        }
        changeDisplayFlag() {
            this.hideFlag = !this.hideFlag;
        }

        isTargetTypeIframe() {
            return this.targetType === "IFRAME";
        }

        isTargetTypeIframeSelected() {
            return this.targetType === "IFRAME" && this.targetLink;
        }

        isTargetTypeLink() {
            return this.targetType === "LINK";
        }

        isTargetTypeInput() {
            return this.targetType === "INPUT";
        }

        isTargetTypePrivacy() {
            return this.targetType === "PRIVACY";
        }

        isTargetTypeScenario() {
            return this.targetType === "SCENARIO";
        }

        isTargetTypeText() {
            return this.targetType === "TEXT";
        }

        isTargetTypeDivide() {
            return this.targetType === "DIVIDE";
        }

        isScenarioSelected() {
            return (
                (this.targetType === "SCENARIO" ||
                    this.targetType === "PRIVACY" ||
                    this.targetType === "INPUT") &&
                this.targetScenarioId
            );
        }

        isScenarioSelectedExceptTarget() {
            return this.targetType === "SCENARIO" && !this.targetScenarioId;
        }

        isTargetTypeDataInput() {
            return this.targetType === "INPUT" || this.targetType === "PRIVACY";
        }

        isTargetTypeEmpty() {
            return !this.targetType;
        }

        isIconRequired() {
            return this.targetType === "SCENARIO" || this.targetType === "LINK";
        }

        isPrivacyContext(context) {
            return this.targetLink?.includes(context);
        }

        isRoate() {
            return this.hideFlag;
        }

        isHide() {
            return this.hideFlag;
        }

        isTargetLink(targetLink) {
            return this.targetLink == targetLink;
        }

        getTittleRows() {
            return this.title?.split("\n").length == undefined
                ? 1
                : this.title?.split("\n").length;
        }

        getButtonTargetType() {
            return this.targetType;
        }

        getTargetScenarioTitle(scenarioId) {
            return bot.defaultChar.getScenario(scenarioId).title;
        }

        getScenarioList() {
            return bot.defaultChar.scenarioList;
        }

        save(func) {
            markSaving();
            PRConnect.send("/api/Bot/modifyButton", { ...this }, (data) => {
                if (data.header.status === "success") {
                    console.log("button update success");
                    bot.applySaved(data);
                }
                bot.assign(false);
                messaging.set({
                    target: "Preview",
                    // 엔터프라이즈 콘텐츠 블록(FIX)은 하드 리로드 대신 소프트 재조회(깜빡임/스크롤 튐 없음).
                    // 그 외(캐릭터/시나리오/2D 등)는 기존대로 iframe 전체 리로드.
                    action:
                        bot.isEnterprise && this.type === "FIX"
                            ? "refreshBlocks"
                            : "refreshPreview",
                    botId: this.botId,
                    charId: this.charId,
                    scenarioId: this.scenarioId,
                });
                if (func) {
                    func(this.id);
                }
            });
        }

        delete() {
            markSaving();
            PRConnect.send("/api/Bot/deleteButton", { id: this.id, botId: this.botId }, (data) => {
                if (data.header.status === "success") {
                    console.log("button delete success");
                    bot.applySaved(data);
                }
                bot.assign(false);
                messaging.set({
                    target: "Preview",
                    // 엔터프라이즈 콘텐츠 블록(FIX) 삭제도 하드 리로드 대신 소프트 재조회.
                    action:
                        bot.isEnterprise && this.type === "FIX"
                            ? "refreshBlocks"
                            : "refreshPreview",
                    botId: this.botId,
                    charId: this.charId,
                    scenarioId: this.scenarioId,
                });
            });
            bot.assign(false);
        }

        deleteImage() {
            this.buttonImageContent = null;
            this.buttonImageType = null;
            this.buttonImageAssetId = null;
            this.save();
        }

        deleteTargetType() {
            this.targetType = null;
            this.targetScenarioId = null;
            this.targetLink = null;
            this.save();
        }

        deleteTargetLink() {
            this.targetLink = null;
            this.save();
        }
    }

    let bot = new Bot(currentRoute.namedParams.botId, planStore);
    onMount(() => {
        const botId = currentRoute.namedParams.botId;

        PRConnect.send(
            "/api/Bot/getBotInfo",
            { id: botId },
            (data) => {
                if (data.header.status === "success") {
                    bot.setPageData(data.result.botDesign);
                    bot.setDate(data.result.botData);
                    bot.setData(data.result.botData);

                    // 허브도 기업형과 같은 셸 경로다(시나리오·모션 없음). 기업형 '전용' 분기와 구분한다.
                    const isShellPage = ["ENTERPRISE", "HUB"].includes(
                        data.result.botData?.pageType,
                    );

                    if (!isShellPage && data.result.botChar) {
                        const botChar = new BotChar(data.result.botChar);
                        botChar.setCharActionList(data.result.charActionList);
                        bot.bot2DChar = botChar;
                        bot.defaultChar = botChar;
                        data.result.scenarioList.forEach((element, idx) => {
                            if (idx === 0) {
                                const active = true;
                                element = { ...element, active };
                            }
                            const scenario = botChar.setScenario(element);
                            scenario.setButtonList(data.result.scenarioButtonList);
                        });
                        botChar.setFixedButtonList(data.result.fixedButtonList);
                        botChar.setRightButtonList(data.result.rightButtonList);
                    } else if (isShellPage) {
                        const botChar = new BotChar(data.result.botChar ?? { botId });
                        botChar.setFixedButtonList(data.result.fixedButtonList);
                        bot.defaultChar = botChar;
                        bot.changeCurrentTab("contents");
                    }

                    bot.assign();
                } else {
                    navigateTo("/main");
                }
            },
            (error) => {
                navigateTo("/main");
            },
        );

        planStoreSubsc = planStore.subscribe((message) => {
            bot.assign();
        });

        // 게시 완료 통지 수신(BuilderLayout이 게시 성공 시 보냄): 방금 전부 게시됐으니
        // bot의 게시일자를 최신 수정일자로 맞춰, sticky '게시하기'의 미반영(pulse) 점과
        // '변경사항 버리기' 활성 상태를 즉시 갱신한다(다음 저장/새로고침을 기다리지 않음).
        builderMsgSubsc = messaging.subscribe((message) => {
            if (message?.target === "Builder" && message.action === "published") {
                bot.lastPostDate = bot.lastUpdateDate;
                bot.assign(false);
            }
        });
    });

    onDestroy(() => {
        planStoreSubsc();
        builderMsgSubsc && builderMsgSubsc();
    });

    // shadcn 셸 탭 정의(봇 빌더). enterprise면 '메인 섹션'은 숨기고 '고정 콘텐츠'는 '페이지'로.
    $: tabList = [
        // 허브는 '무엇이 보이나'(로고·캐릭터·소개 문구·단계별 멘트)를 첫 탭에 모은다.
        // 허브를 새로 만든 사람이 여기부터 채우면 화면이 완성된다.
        ...(bot.isHub ? [{ label: "기본", value: "hubBasic", required: false }] : []),
        // 시나리오는 2D 캐릭터형에만 있다(기업형·허브는 셸 렌더라 시나리오 개념이 없다)
        ...(bot.isShellPage
            ? []
            : [{ label: "메인 섹션 구성", value: "scenario", required: false }]),
        {
            label: bot.isHub ? "채팅" : bot.isEnterprise ? "페이지" : "고정 콘텐츠",
            value: "contents",
            // 같은 탭 자리지만 허브는 링크가 아니라 카드 격자를 다룬다 → 아이콘도 갈라준다.
            // (아이콘 맵은 value 기준이라, 라벨이 pageType 마다 다른 탭은 여기서 키를 준다)
            icon: bot.isHub ? "hubContents" : undefined,
            required: false,
        },
        { label: "이미지 에셋", value: "imageAsset", required: false },
        { label: "디자인", value: "pageDesign", required: false },
        { label: "통계", value: "statistics", required: false },
        // 허브에만 있다 — 담긴 챗들의 AI 사용량·추정 비용(LLM + 음성). 방문 통계와 같은 '보는' 탭이라 바로 옆에 둔다.
        ...(bot.isHub
            ? [{ label: "사용량·비용", value: "hubUsage", required: false }]
            : []),
        { label: "설정", value: "statisticsManagement", required: false },
        { label: "수집", value: "customerResponse", required: false },
        // 허브에만 있다(납품처에 계정을 발급하는 건 허브 운영 개념이다).
        // ⚠️ 맨 아래에 둔다 — 저작(기본·채팅)과 성격이 다른 '운영' 업무라 저작 흐름을 끊지 않게.
        //    라벨은 '허브관리자'다 — 내부 이름(HUB_ADMINS · hub-admin 서브도메인 · hubAdminSession)과
        //    같은 말이라 코드와 화면이 어긋나지 않는다. 다루는 대상은 **납품처 기관의 운영자 계정**이다
        //    (빌더 소유자 본인 계정이 아니다 — 신원 3계층은 설계문서 §7 참고).
        ...(bot.isHub
            ? [{ label: "허브관리자", value: "hubOrgs", required: false }]
            : []),
    ];

    // 콘텐츠 헤더에 표시할 현재 탭 라벨(bot 재할당 시 갱신)
    $: currentTabLabel =
        (tabList.find((t) => t.value === bot.currentTab) || {}).label || "";

    // 반응형: 좁은 화면(≤1023px)에선 프리뷰를 사이드가 아니라 오버레이 드로어로.
    onMount(() => {
        const mql = window.matchMedia("(max-width: 1023px)"); // Tailwind lg(≥1024)와 경계 일치
        const apply = () => {
            isNarrow = mql.matches;
            previewOpen = !mql.matches; // 넓으면 항상 표시, 좁으면 기본 닫힘
        };
        apply();
        mql.addEventListener("change", apply);
        return () => mql.removeEventListener("change", apply);
    });

</script>

<div class="builder-shell" in:fade>
    <div class="tw-mx-auto tw-flex tw-h-full tw-w-full tw-overflow-hidden tw-bg-background tw-font-sans tw-text-foreground" style="max-width: var(--app-max-w, 1920px)">
        <!-- 좌측 섹션 네비게이션(태블릿=아이콘 레일 / 데스크톱=풀 사이드바). 폰은 아래 BuilderTopTabs로 대체 -->
        <BuilderSidebar {bot} {tabList} />

        <!-- 에디터+프리뷰를 가운데로 모으고 양옆 여백 -->
        <div class="tw-flex tw-min-w-0 tw-flex-1 tw-justify-center tw-overflow-hidden tw-bg-background">
            <div class="tw-flex tw-w-full tw-min-w-0">
                <!-- 에디터: 메인 작업영역 -->
                <main class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
                    <!-- 폰(<768px): 좌측 사이드바 대신 상단 가로 탭 -->
                    <BuilderTopTabs {bot} {tabList} />
                    <div class="builder-editor-scroll tw-flex-1 tw-overflow-y-auto">
                        <!-- 스티키 액션 툴바: 현재 탭 라벨 + 자동 저장 상태 -->
                        <div class="tw-sticky tw-top-0 tw-z-20 tw-border-b tw-border-border tw-bg-background/90 tw-backdrop-blur">
                            <div class="tw-mx-auto tw-flex tw-w-full tw-max-w-[860px] tw-items-center tw-justify-between tw-gap-3 tw-px-6 tw-py-2.5">
                                <div class="tw-min-w-0">
                                    <div class="tw-hidden tw-truncate tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-muted-foreground sm:tw-block">{bot.title || "아바톡"} · 빌더</div>
                                    <h1 class="tw-truncate tw-text-base tw-font-semibold tw-text-foreground sm:tw-text-lg">{currentTabLabel}</h1>
                                </div>
                                <!-- 게시 액션(BuilderLayout 헤더 → 이 sticky 툴바로 이동, ChatBuilder와 통일):
                                     자동 저장 상태 pill + [변경사항 버리기] + [게시하기].
                                     게시/폐기 다이얼로그는 BuilderLayout이 소유 → messaging으로 열기 요청. -->
                                <div class="tw-relative tw-flex tw-shrink-0 tw-items-center tw-gap-2 sm:tw-gap-3">
                                <div
                                    class="save-indicator tw-inline-flex tw-select-none tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-transition-colors {$saveStatus ===
                                    'saving'
                                        ? 'tw-border-border tw-bg-muted tw-text-muted-foreground'
                                        : $saveStatus === 'saved'
                                            ? 'tw-border-emerald-500/30 tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-400'
                                            : 'tw-border-transparent tw-bg-muted/50 tw-text-muted-foreground/70'}"
                                    aria-live="polite"
                                >
                                    {#if $saveStatus === "saving"}
                                        <svg class="save-spinner tw-h-3.5 tw-w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                                        <span>저장 중…</span>
                                    {:else if $saveStatus === "saved"}
                                        <svg class="save-check tw-h-3.5 tw-w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                        <span>저장됨</span>
                                    {:else}
                                        <svg class="tw-h-3.5 tw-w-3.5 tw-opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                        <span>저장됨</span>
                                    {/if}
                                </div>
                                <!-- 변경사항 버리기: 미반영 변경이 있을 때만 활성 → BuilderLayout 폐기 다이얼로그 열기 요청 -->
                                <button
                                    type="button"
                                    class="tw-inline-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-px-3 tw-text-sm tw-font-medium tw-text-muted-foreground tw-transition-colors hover:tw-bg-muted hover:tw-text-foreground disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
                                    disabled={!bot.isLatestUpdated()}
                                    on:click={() => messaging.set({ target: "BuilderLayout", action: "openDisposal" })}
                                >변경사항 버리기</button>
                                <!-- 게시하기: BuilderLayout 게시 다이얼로그 열기 요청. 미반영 변경 있으면 점 표시 -->
                                <button
                                    type="button"
                                    class="tw-inline-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-md tw-bg-primary tw-px-4 tw-text-sm tw-font-medium tw-text-primary-foreground tw-transition-colors hover:tw-bg-primary/90"
                                    on:click={() => messaging.set({ target: "BuilderLayout", action: "openPublish" })}
                                >
                                    {#if bot.isLatestUpdated()}
                                        <span class="publish-dot tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-foreground" title="게시되지 않은 변경사항이 있어요"></span>
                                    {/if}
                                    게시하기
                                </button>
                                </div>
                            </div>
                        </div>
                        <div class="tw-mx-auto tw-w-full tw-max-w-[860px] tw-px-6 tw-pb-6 tw-pt-5">
                            <!-- 각 탭 컴포넌트는 내부에서 bot.currentTab로 표시/숨김(show/hide) -->
                            <DialogueScenario {bot} />
                            <ContentsBlock {bot} />
                            {#if bot.isHub && bot.isCurrentTab("hubBasic")}
                                <div class="tw-space-y-4 tw-font-sans tw-text-foreground">
                                    <HubBasic {bot} />
                                </div>
                            {/if}
                            {#if bot.isHub && bot.isCurrentTab("hubUsage")}
                                <div class="tw-font-sans tw-text-foreground">
                                    <HubUsage botId={bot.id} />
                                </div>
                            {/if}
                            {#if bot.isHub && bot.isCurrentTab("hubOrgs")}
                                <div class="tw-font-sans tw-text-foreground">
                                    <!-- 지금 허브 기준으로 기관을 보여 준다(이 허브 기관 먼저, 새 기관은 이 허브에 바로 연결) -->
                                    <HubOrgAdmin botId={bot.id} />
                                </div>
                            {/if}
                            <BotImageAsset {bot} />
                            <PageDesign {bot} />
                            {#if bot.isCurrentTab("statistics")}
                                <StatisticsDashboard
                                    botId={bot.id}
                                    locked={!bot.isAvailableType("tabType", "statisticsManagement")}
                                />
                            {/if}
                            <StatisticsManagement {bot} />
                            {#if bot.isCurrentTab("customerResponse")}
                                <CollectionDashboard source="bot" botId={bot.id} />
                            {/if}
                        </div>
                    </div>
                </main>

                <!-- 좁은 화면: 프리뷰 닫혀 있을 때 떠 있는 열기 버튼 -->
                {#if isNarrow && !previewOpen}
                    <button
                        type="button"
                        class="tw-fixed tw-bottom-5 tw-right-5 tw-z-[998] tw-inline-flex tw-h-11 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-primary tw-px-4 tw-text-sm tw-font-medium tw-text-primary-foreground tw-shadow-lg tw-transition-colors hover:tw-bg-primary/90"
                        on:click={() => (previewOpen = true)}
                        title="미리보기 열기"
                    >🔎 미리보기</button>
                {/if}

                <!-- 좁은 화면 드로어 배경(탭하면 닫힘) -->
                {#if isNarrow && previewOpen}
                    <button
                        type="button"
                        class="tw-fixed tw-inset-0 tw-z-[999] tw-cursor-default tw-bg-black/40"
                        on:click={() => (previewOpen = false)}
                        aria-label="미리보기 닫기"
                    ></button>
                {/if}

                <!-- 미리보기: 넓은 화면=사이드 / 좁은 화면(≤1023px)=오른쪽 슬라이드 드로어 -->
                <aside
                    class="tw-flex tw-flex-col tw-bg-background {isNarrow
                        ? 'tw-fixed tw-inset-y-0 tw-right-0 tw-z-[1000] tw-w-[min(440px,100vw)] tw-border-l tw-border-border tw-shadow-2xl tw-transition-transform ' +
                            (previewOpen ? 'tw-translate-x-0' : 'tw-translate-x-full')
                        : 'tw-w-[460px] tw-shrink-0 tw-border-l tw-border-border'}"
                    aria-hidden={isNarrow && !previewOpen}
                >
                    <!-- 프리뷰를 shadcn 카드로 감싼다: 에디터의 Section 카드들과 같은 언어(rounded-xl·
                         border·bg-card·shadow-sm)로 통일 → 공유 캔버스(bg-background) 위에 카드가 떠 있는
                         구조가 되어 "동떨어진" 느낌이 사라진다. 제목은 카드 헤더(좌측·작게)로 자리 잡음. -->
                    <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-p-4">
                        <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-border tw-bg-card tw-shadow-sm">
                            <header class="tw-flex tw-min-h-[44px] tw-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-border tw-px-4 tw-py-2">
                                <h2 class="tw-truncate tw-text-sm tw-font-semibold tw-text-foreground">미리보기</h2>
                                {#if isNarrow}
                                    <button
                                        type="button"
                                        class="tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground tw-transition-colors hover:tw-bg-muted hover:tw-text-foreground"
                                        on:click={() => (previewOpen = false)}
                                        aria-label="미리보기 닫기"
                                        title="미리보기 닫기"
                                    >
                                        <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                    </button>
                                {/if}
                            </header>
                            <div class="tw-flex tw-min-h-0 tw-flex-1 tw-items-start tw-justify-center tw-overflow-auto">
                                <Preview {bot} />
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    </div>
</div>
<Plan />

<style>
    /* 새 빌더 셸: BuilderLayout의 상단 톱바(.builder-topbar = position:fixed, 높이 56px) 아래를
       뷰포트 끝까지 채운다. 톱바가 흐름에서 빠져 있으므로 margin-top으로 밀어 겹침 방지. */
    .builder-shell {
        margin-top: 56px;
        height: calc(100vh - 56px);
        width: 100%;
        overflow: hidden;
        /* 캡된 콘텐츠 좌우 여백을 토큰 배경으로 채워 다크/라이트 동일하게 */
        background: hsl(var(--sb-background));
    }

    /* 자동 저장 인디케이터 */
    @keyframes sb-spin {
        to {
            transform: rotate(360deg);
        }
    }
    .save-spinner {
        animation: sb-spin 0.7s linear infinite;
    }
    /* '저장됨'이 나타날 때 살짝 튀어오르며 페이드 인 */
    @keyframes sb-pop {
        0% {
            opacity: 0;
            transform: scale(0.8);
        }
        60% {
            transform: scale(1.08);
        }
        100% {
            opacity: 1;
            transform: scale(1);
        }
    }
    .save-check {
        animation: sb-pop 0.28s ease-out;
    }
    .save-indicator > span {
        animation: sb-fade 0.2s ease-out;
    }
    @keyframes sb-fade {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
    /* 게시 대기(미반영 변경) 점: 은은한 맥동으로 시선을 끈다 (ChatBuilder와 동일) */
    @keyframes sb-pulse {
        0%,
        100% {
            opacity: 1;
            transform: scale(1);
        }
        50% {
            opacity: 0.45;
            transform: scale(0.85);
        }
    }
    .publish-dot {
        animation: sb-pulse 1.6s ease-in-out infinite;
    }
    @media (prefers-reduced-motion: reduce) {
        .save-spinner,
        .save-check,
        .save-indicator > span,
        .publish-dot {
            animation: none;
        }
    }
</style>
